<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <!-- HEADER -->
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title>Completion Reports</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- TEMPLATE DOWNLOAD -->
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="white" small text v-bind="attrs" v-on="on">
              <v-icon left>mdi-file-download</v-icon> Template
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="downloadTemplate('xlsx')">
              <v-list-item-icon class="mr-2"><v-icon>mdi-file-excel</v-icon></v-list-item-icon>
              <v-list-item-title>XLSX template</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadTemplate('json')">
              <v-list-item-icon class="mr-2"><v-icon>mdi-code-json</v-icon></v-list-item-icon>
              <v-list-item-title>JSON template</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn color="white" small text @click="importDialog = true">
          <v-icon left>mdi-database-import</v-icon> Import
        </v-btn>

        <v-btn color="white" small text :to="'/completion-reports/create'">
          <v-icon left>mdi-plus</v-icon> New Report
        </v-btn>
      </v-toolbar>

      <import-dialog v-model="importDialog" @imported="fetchData" />

      <!-- FILTERS -->
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field dense outlined hide-details label="Search (Ref No / Project)" v-model="search"
              clearable prepend-inner-icon="mdi-magnify" />
          </v-col>
        </v-row>

        <!-- TABLE -->
        <v-data-table :headers="headers" :items="data" :loading="loading" :options.sync="options"
          :server-items-length="total" :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }" dense>
          <!-- DATE -->
          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>

          <!-- CREATED -->
          <template v-slot:item.created_at="{ item }">
            {{ formatTimestamp(item.created_at) }}
          </template>

          <!-- LOCK STATUS -->
          <template v-slot:item.locked_at="{ item }">
            <v-chip :color="item.locked_at ? 'red' : 'green'" small dark>
              {{ item.locked_at ? 'Locked' : 'Open' }}
            </v-chip>
          </template>

          <!-- ACTIONS -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="view(item.id)">
              <v-icon color="purple">mdi-eye</v-icon>
            </v-btn>

            <v-btn icon small :disabled="item.locked_at" @click="edit(item.id)">
              <v-icon color="yellow darken-3">mdi-pencil</v-icon>
            </v-btn>

            <!-- Duplicating never touches the source, so locked reports qualify. -->
            <v-btn icon small @click="duplicate(item.id)">
              <v-icon color="blue darken-2">mdi-content-duplicate</v-icon>
            </v-btn>

            <v-btn icon small color="red" :disabled="item.locked_at" @click="remove(item.id, item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>

            <v-btn icon small @click="print(item.id, item.ref_no)">
              <v-icon color="green">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-data-table>

      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import _ from 'lodash'
import appAxios from '@/libs/app-axios'
import { LoadingPopup, ErrorPopup, SuccessPopup, formatDateTimeUS } from '@/libs/utils'
import ImportDialog from './ImportDialog.vue'

export default {
  name: 'CompletionReportsIndex',

  components: { ImportDialog },

  data() {
    return {
      data: [],
      total: 0,
      search: '',
      lastSearch: '',
      loading: false,
      importDialog: false,

      options: {
        page: 1,
        itemsPerPage: 10,
      },

      headers: [
        { text: 'Ref No', value: 'ref_no' },
        { text: 'Project', value: 'project_name' },
        { text: 'Date', value: 'date' },
        // Display only — the API always orders by ref_no and ignores sortBy.
        { text: 'Created', value: 'created_at', sortable: false },
        { text: 'Customer', value: 'customer_id' },
        { text: 'Status', value: 'locked_at' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],

    }
  },

  watch: {
    options: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },

    search: {
      handler: _.debounce(function (val) {
        const trimmed = (val || '').trim()
        if (trimmed === this.lastSearch) return

        this.lastSearch = trimmed
        this.options.page = 1
        this.fetchData()
      }, 400),
    },
  },

  methods: {
    // `date` arrives as a plain Y-m-d string. new Date('2026-08-09') parses as
    // UTC midnight, which renders the previous day on negative-offset browsers,
    // so build the Date from its parts.
    formatDate(d) {
      if (!d) return '-'
      const [year, month, day] = String(d).slice(0, 10).split('-').map(Number)
      if (!year || !month || !day) return '-'
      return new Date(year, month - 1, day).toLocaleDateString()
    },

    // created_at is a real instant (serialized as UTC). Pin it to office time so
    // the column reads the same regardless of where it's viewed from.
    formatTimestamp(ts) {
      if (!ts) return '-'
      return formatDateTimeUS(new Date(ts), 'Asia/Manila')
    },

    async downloadTemplate(format) {
      try {
        const { data } = await appAxios().get('/v3/completion-reports/import/template', {
          params: { format },
          responseType: 'blob',
        })
        const ext = format === 'json' ? 'json' : 'xlsx'
        const url = URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `completion-report-import-template.${ext}`
        link.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to download template')
      }
    },

    async fetchData() {
      try {
        this.loading = true
        const { page, itemsPerPage } = this.options
        const res = await appAxios().get('/v3/completion-reports', {
          params: {
            page,
            limit: itemsPerPage,
            search: this.search,
          },
        })
        this.data = res.data.paginated.data
        this.total = res.data.paginated.total
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to fetch data')
      } finally {
        this.loading = false
      }
    },

    view(id) {
      this.$router.push(`/completion-reports/${id}/show`)
    },

    edit(id) {
      this.$router.push(`/completion-reports/${id}/edit`)
    },

    duplicate(id) {
      this.$router.push({
        name: 'CompletionReports.Create',
        query: { duplicateFrom: id },
      })
    },

    async print(id, refNo) {
      try {
        const { data } = await appAxios().get(`/v3/completion-reports/${id}/pdf`, {
          responseType: 'blob',
        })
        const fileURL = URL.createObjectURL(
          new Blob([data], { type: 'application/pdf' })
        )
        const link = document.createElement('a')
        link.href = fileURL
        link.download = `CompletionReport-${refNo}.pdf`
        link.click()
        window.open(fileURL, '_blank')
        URL.revokeObjectURL(fileURL)
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(e)
        }
        ErrorPopup.fire('Oops something went wrong while retrieving the report')
      }
    },

    async remove(id, item) {
      try {
        const confirmText = `DELETE ${item.ref_no}`

        const input = prompt(
          `This action is irreversible.\n\nType:\n${confirmText}\n\nto confirm deletion.`
        )

        if (!input || input.trim() !== confirmText) {
          ErrorPopup.fire('Confirmation text did not match. Deletion cancelled.')
          return
        }

        LoadingPopup.fire('Deleting...')
        await appAxios().delete(`/v3/completion-reports/${id}`)
        LoadingPopup.close()

        SuccessPopup.fire('Deleted')
        this.fetchData()
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        ErrorPopup.fire('Failed to delete')
      }
    }

  },
}
</script>
